import axios from 'axios';
import { BASE_URL } from '../constants/url';

export const TOKEN_KEY = 'creditflow_access_token';
export const REFRESH_KEY = 'creditflow_refresh_token';
export const USER_KEY = 'creditflow_user';

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' }
});

// A bare client for the refresh call itself — using axiosInstance would
// re-enter the interceptor below and recurse.
const refreshClient = axios.create({ baseURL: BASE_URL });

export const clearSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
};

// ---- Request: attach the current token ----
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ---- Response: refresh once, retry, or give up ----
//
// Single-flight is essential here. The backend ROTATES refresh tokens —
// each one is single-use. If three requests 401 at the same time and each
// tries to refresh, the first succeeds and the other two send a token that
// has already been burned, so they 401 and log the user out.
// One promise, shared by every waiting request.
let refreshPromise = null;

const runRefresh = async () => {
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) throw new Error('No refresh token');

  const { data } = await refreshClient.post('/auth/refresh', { refreshToken });
  const payload = data.data;

  localStorage.setItem(TOKEN_KEY, payload.accessToken);

  // Rotation: the server issued a new refresh token. Store it or the
  // next refresh will fail.
  if (payload.refreshToken) {
    localStorage.setItem(REFRESH_KEY, payload.refreshToken);
  }
  if (payload.user) {
    localStorage.setItem(USER_KEY, JSON.stringify(payload.user));
  }

  return payload.accessToken;
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const code = error.response?.data?.code;

    const isExpired = status === 401 && code === 'TOKEN_EXPIRED';
    const isRefreshCall = original?.url?.includes('/auth/refresh');

    // Only retry once, only for expiry, never for the refresh call itself.
    if (!isExpired || isRefreshCall || original?._retried) {
      if (status === 401 && !isRefreshCall) {
        clearSession();
        window.dispatchEvent(new Event('creditflow:session-expired'));
      }
      return Promise.reject(error);
    }

    original._retried = true;

    try {
      if (!refreshPromise) {
        refreshPromise = runRefresh().finally(() => {
          refreshPromise = null;
        });
      }

      const newToken = await refreshPromise;
      original.headers.Authorization = `Bearer ${newToken}`;
      return axiosInstance(original);
    } catch (refreshError) {
      clearSession();
      window.dispatchEvent(new Event('creditflow:session-expired'));
      return Promise.reject(refreshError);
    }
  }
);

export default axiosInstance;
